import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Loader from "components/Loader";
import { FORM_ERROR } from "final-form";
import { makeStyles } from "helpers/StyleHelper";
import getUserDisplayName from "helpers/userHelper";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useEffect, useMemo, useState } from "react";
import { Field, Form } from "react-final-form";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  IMergeUserRequest,
  MergeUserRequest,
  OrganizationUserDetails,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import serializableError from "utils/serializableError";
import translateError from "utils/translateError";

type RouteParamsType = {
  idFrom: string;
  idTo: string;
};

const useStyles = makeStyles({
  paper: {
    padding: "15px",
  },
  mergeButton: {
    width: "100%",
    cursor: "pointer",
  },
});

const UserConfirmMerge = () => {
  const { formatMessage: t } = useIntl();
  const params = useParams<RouteParamsType>();
  const { registerPromise } = useTrashablePromises();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    usersListUrl,
    userStartMergeUrl,
    organizationUserService,
    userService,
  } = useGlobalContext();
  const [confirmMergeDialogOpen, setConfirmMergeDialogOpen] = useState(false);
  const [mergeInProgress, setMergeInProgress] = useState(false);
  const [errors, setErrors] = useState<Array<any>>([]);
  const styles = useStyles(useTheme());

  useEffect(() => {
    contextStore.update((s) => {
      s.organizationIdDisabled = true;
    });

    return () => {
      contextStore.update((s) => {
        s.organizationIdDisabled = false;
      });
    };
  }, []);

  const userToDeleteQuery = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", params.idFrom],
    () => organizationUserService.getOrganizationUserDetails(params.idFrom!)
  );

  const userToUpdateQuery = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", params.idTo],
    () => organizationUserService.getOrganizationUserDetails(params.idTo!)
  );

  useEffect(() => {
    if (userToDeleteQuery.isError) {
      setErrors((errors) => [
        ...errors,
        serializableError(userToDeleteQuery.error),
      ]);
    }
    if (userToUpdateQuery.isError) {
      setErrors((errors) => [
        ...errors,
        serializableError(userToUpdateQuery.error),
      ]);
    }
  }, [
    userToDeleteQuery.isError,
    userToDeleteQuery.error,
    userToUpdateQuery.isError,
    userToUpdateQuery.error,
  ]);

  const userToDelete = userToDeleteQuery.data;
  const userToUpdate = userToUpdateQuery.data;

  const nameToDelete = getUserDisplayName(userToDelete, "");
  const nameToUpdate = getUserDisplayName(userToUpdate, "");

  useTitle(t({ id: "mergeUsersTitle" }, { nameToDelete, nameToUpdate }));

  const init = useMemo<MergeUserRequest>(() => {
    return MergeUserRequest.fromJS({
      originalUserId: userToDelete?.id,
      newUserId: userToUpdate?.id,
      reason: "",
    });
  }, [userToDelete, userToUpdate]);

  if (userToDeleteQuery.isFetching || userToUpdateQuery.isFetching) {
    return <Loader immediate />;
  }

  if (
    !userToDelete ||
    !userToUpdate ||
    userToDelete.organizationId !== userToUpdate.organizationId
  ) {
    return <Navigate to={userStartMergeUrl(params.idFrom)} replace />;
  }

  const submitUsersForMergeAsync = async (values: IMergeUserRequest) => {
    try {
      setMergeInProgress(true);

      const mergeUserRequest = new MergeUserRequest(values);

      await registerPromise(userService.mergeUser(mergeUserRequest));
      queryClient.invalidateQueries("searchOrganizationUsers");
      queryClient.invalidateQueries([
        "getOrganizationUserDetails",
        userToDelete.id,
      ]);
      queryClient.invalidateQueries([
        "getOrganizationUserDetails",
        userToUpdate.id,
      ]);

      setConfirmMergeDialogOpen(false);
      contextStore.update((s) => {
        s.organizationId = undefined;
      });

      navigate(usersListUrl());
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    } finally {
      setMergeInProgress(false);
    }
  };

  const validateForm = (values: MergeUserRequest) => {
    const errors = {} as any;

    if (!values.reason?.trim()) {
      errors.reason = t({ id: "mergingReasonRequiredMessage" });
    }

    return errors;
  };

  if (errors.length > 0) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(errors, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Paper variant="outlined" sx={styles.paper}>
            <Typography variant="h5" id="tableTitle" component="div">
              {t({ id: "mergeUserToBeDeleted" })}
            </Typography>
            <Typography variant="h6" id="tableTitle" component="div">
              {`${t({ id: "emailLabel" })}: ${userToDelete.email}`}
            </Typography>
            <Typography variant="h6" id="tableTitle" component="div">
              {`${t({ id: "firstNameLabel" })}: ${userToDelete.firstName}`}
            </Typography>
            <Typography variant="h6" id="tableTitle" component="div">
              {`${t({ id: "lastNameLabel" })}: ${userToDelete.lastName}`}
            </Typography>
            <Typography variant="h6" id="tableTitle" component="div">
              {`${t({ id: "externalSourceId" })}: ${
                userToDelete.externalSourceId
              }`}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper variant="outlined" sx={styles.paper}>
            <Typography variant="h5" id="tableTitle" component="div">
              {t({ id: "mergeUserToMergeDataTo" })}
            </Typography>
            <Typography variant="h6" id="tableTitle" component="div">
              {`${t({ id: "emailLabel" })}: ${userToUpdate.email}`}
            </Typography>
            <Typography variant="h6" id="tableTitle" component="div">
              {`${t({ id: "firstNameLabel" })}: ${userToUpdate.firstName}`}
            </Typography>
            <Typography variant="h6" id="tableTitle" component="div">
              {`${t({ id: "lastNameLabel" })}: ${userToUpdate.lastName}`}
            </Typography>
            <Typography variant="h6" id="tableTitle" component="div">
              {`${t({ id: "externalSourceId" })}: ${
                userToUpdate.externalSourceId
              }`}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="outlined"
            sx={styles.mergeButton}
            onClick={() => setConfirmMergeDialogOpen(true)}
          >
            {t({ id: "mergeUsers" })}
          </Button>
        </Grid>
      </Grid>
      <Dialog
        open={confirmMergeDialogOpen}
        disableEnforceFocus
        maxWidth="sm"
        fullWidth
      >
        <Form
          initialValues={init}
          onSubmit={submitUsersForMergeAsync}
          validate={validateForm}
          render={({ handleSubmit, submitError }) => (
            <form onSubmit={handleSubmit}>
              <DialogTitle>{t({ id: "confirmOperation" })}</DialogTitle>
              <DialogContent>
                <Typography paragraph>
                  {t({ id: "mergeAreYouSureText" })}
                </Typography>
                <Typography paragraph>
                  {t({ id: "provideMergeReason" })}
                </Typography>
                <Grid container spacing={2}>
                  <Field name="reason" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          placeholder={t({ id: "mergingReason" })}
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>
                  
                  {mergeInProgress && (
                    <Grid item xs={12}>
                      <Loader immediate margin={0} />
                    </Grid>
                  )}

                  {submitError && (
                    <Grid item xs={12}>
                      <Alert severity="error">{submitError}</Alert>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button disabled={mergeInProgress} type="submit">
                  {t({ id: "yes" })}
                </Button>
                <Button
                  disabled={mergeInProgress}
                  onClick={() => setConfirmMergeDialogOpen(false)}
                >
                  {t({ id: "no" })}
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

export default UserConfirmMerge;
